import React, { useState } from "react";
import { Button, Form } from "react-bootstrap";

// Fixed tank sizes. Each maps to its own qty / cost-per columns on the service.
// Exchange only ever deals in 20# tanks; Refill also takes 30#, 100# and one
// custom size.
const FIXED_ROWS = [
  { label: "20#", count: "propane_tank_count", cost: "propane_cost_per", refillOnly: false },
  { label: "30#", count: "propane_tank_count_30", cost: "propane_cost_per_30", refillOnly: true },
  { label: "100#", count: "propane_tank_count_100", cost: "propane_cost_per_100", refillOnly: true },
];

const CUSTOM_FIELDS = {
  propane_custom_size_lbs: "",
  propane_tank_count_custom: "",
  propane_cost_per_custom: "",
};

// Form fields that only apply to a Refill. Cleared when the service switches,
// so an Exchange never carries hidden refill quantities into the totals.
export const REFILL_ONLY_FIELDS = {
  propane_tank_count_30: "",
  propane_cost_per_30: "",
  propane_tank_count_100: "",
  propane_cost_per_100: "",
  ...CUSTOM_FIELDS,
};

function lineTotal(qty, cost) {
  return qty && cost ? `$${(parseFloat(qty) * parseFloat(cost)).toFixed(2)}` : "—";
}

/**
 * Size / Qty / Cost Per / Total table for a propane Tank service.
 * `form` is the service form state; `setForm` its updater.
 */
export default function PropaneTankTable({ form, setForm }) {
  const isRefill = form.propane_tank_service === "Refill";
  const hasCustom = !!(
    form.propane_custom_size_lbs || form.propane_tank_count_custom || form.propane_cost_per_custom
  );
  const [customOpen, setCustomOpen] = useState(false);
  const showCustom = isRefill && (customOpen || hasCustom);

  const set = (key) => (e) => setForm((p) => ({ ...p, [key]: e.target.value }));

  const removeCustom = () => {
    setForm((p) => ({ ...p, ...CUSTOM_FIELDS }));
    setCustomOpen(false);
  };

  const qtyInput = (key) => (
    <Form.Control
      type="number" min="0" step="1" style={{ width: "80px" }}
      value={form[key] ?? ""}
      onChange={set(key)}
      placeholder="0"
    />
  );
  const costInput = (key) => (
    <Form.Control
      type="number" min="0" step="0.01" style={{ width: "100px" }}
      value={form[key] ?? ""}
      onChange={set(key)}
      placeholder="0.00"
    />
  );
  const totalCell = (qtyKey, costKey) => (
    <Form.Control
      type="text" readOnly style={{ width: "100px" }}
      value={lineTotal(form[qtyKey], form[costKey])}
    />
  );

  return (
    <div className="mb-3">
      <table style={{ borderSpacing: "0 6px", borderCollapse: "separate" }}>
        <thead>
          <tr>
            <th className="pe-3 text-muted fw-normal" style={{ fontSize: "0.85em", minWidth: 48 }}>Size</th>
            <th className="pe-3 text-muted fw-normal" style={{ fontSize: "0.85em" }}>Qty</th>
            <th className="pe-3 text-muted fw-normal" style={{ fontSize: "0.85em" }}>Cost Per ($)</th>
            <th className="text-muted fw-normal" style={{ fontSize: "0.85em" }}>Total</th>
          </tr>
        </thead>
        <tbody>
          {FIXED_ROWS.filter((r) => isRefill || !r.refillOnly).map((r) => (
            <tr key={r.label}>
              <td className="pe-3 align-middle fw-semibold">{r.label}</td>
              <td className="pe-3">{qtyInput(r.count)}</td>
              <td className="pe-3">{costInput(r.cost)}</td>
              <td>{totalCell(r.count, r.cost)}</td>
            </tr>
          ))}
          {showCustom && (
            <tr>
              <td className="pe-3">
                <div className="d-flex align-items-center gap-1">
                  <Form.Control
                    type="number" min="1" step="1" style={{ width: "70px" }}
                    value={form.propane_custom_size_lbs ?? ""}
                    onChange={set("propane_custom_size_lbs")}
                    placeholder="lbs"
                    aria-label="Custom tank size in pounds"
                  />
                  <span className="fw-semibold">#</span>
                </div>
              </td>
              <td className="pe-3">{qtyInput("propane_tank_count_custom")}</td>
              <td className="pe-3">{costInput("propane_cost_per_custom")}</td>
              <td>
                <div className="d-flex align-items-center gap-1">
                  {totalCell("propane_tank_count_custom", "propane_cost_per_custom")}
                  <Button
                    variant="link"
                    size="sm"
                    className="text-danger p-0 ms-1"
                    onClick={removeCustom}
                    title="Remove custom size"
                    aria-label="Remove custom size"
                  >
                    <i className="bi bi-x-circle" />
                  </Button>
                </div>
              </td>
            </tr>
          )}
        </tbody>
      </table>
      {isRefill && !showCustom && (
        <Button variant="link" size="sm" className="p-0" onClick={() => setCustomOpen(true)}>
          <i className="bi bi-plus-circle me-1" />Add custom size
        </Button>
      )}
    </div>
  );
}
